import React, { useState, useEffect } from "react";
import { UploadedFile } from "@/entities/UploadedFile";
import { ApiRequest } from "@/entities/ApiRequest";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Upload,
  FileText,
  Send,
  File,
  ArrowUpRight,
  BarChart2,
  Clock,
  HardDrive,
  Activity,
  History,
  Plus
} from "lucide-react";
import { motion } from "framer-motion";

export default function Dashboard() {
  const [files, setFiles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    totalFiles: 0,
    totalRequests: 0,
    totalStorage: 0,
    recentActivity: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load recent files
        const recentFiles = await UploadedFile.list("-created_date", 5);
        setFiles(recentFiles);

        // Load recent API requests
        const recentRequests = await ApiRequest.list("-created_date", 5);
        setRequests(recentRequests);

        // Get all files for stats
        const allFiles = await UploadedFile.list();
        const allRequests = await ApiRequest.list();

        // Create combined activity feed
        const activities = [
          ...allFiles.map(file => ({
            type: 'file',
            item: file,
            date: new Date(file.created_date)
          })),
          ...allRequests.map(req => ({
            type: 'request',
            item: req,
            date: new Date(req.created_date)
          }))
        ];

        activities.sort((a, b) => b.date - a.date);

        setStats({
          totalFiles: allFiles.length,
          totalRequests: allRequests.length,
          totalStorage: allFiles.reduce((total, file) => total + (file.file_size || 0), 0),
          recentActivity: activities.slice(0, 8)
        });
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      }
      setIsLoading(false);
    };

    loadData();
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-800';
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-purple-100 text-purple-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Visão geral das suas atividades
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Total de Arquivos</p>
                <h3 className="text-2xl font-bold">{stats.totalFiles}</h3>
              </div>
              <div className="p-3 bg-blue-50 rounded-full">
                <HardDrive className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <Link to={createPageUrl("Upload")}>
                <Button variant="outline" className="w-full text-sm">
                  <Upload className="w-4 h-4 mr-2" />
                  Fazer Upload
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">API Requisições</p>
                <h3 className="text-2xl font-bold">{stats.totalRequests}</h3>
              </div>
              <div className="p-3 bg-sky-50 rounded-full">
                <Activity className="w-6 h-6 text-sky-600" />
              </div>
            </div>
            <div className="mt-4">
              <Link to={createPageUrl("ApiTester")}>
                <Button variant="outline" className="w-full text-sm">
                  <Send className="w-4 h-4 mr-2" />
                  Testar API
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Espaço Utilizado</p>
                <h3 className="text-2xl font-bold">{formatFileSize(stats.totalStorage)}</h3>
              </div>
              <div className="p-3 bg-green-50 rounded-full">
                <BarChart2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <Progress value={Math.min((stats.totalStorage / 10485760) * 100, 100)} className="h-2" />
              <p className="text-xs text-gray-500 mt-2">
                {((stats.totalStorage / 10485760) * 100).toFixed(1)}% de 10 MB
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Atividade Recente</p>
                <h3 className="text-2xl font-bold">{stats.recentActivity.length}</h3>
              </div>
              <div className="p-3 bg-purple-50 rounded-full">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4">
              <div className="grid grid-cols-4 gap-1">
                {stats.recentActivity.slice(0, 4).map((activity, index) => (
                  <div 
                    key={index} 
                    className={`h-2 rounded-full ${activity.type === 'file' ? 'bg-blue-400' : 'bg-indigo-400'}`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {isLoading ? 'Carregando...' : `Última atividade: ${stats.recentActivity[0]?.date.toLocaleTimeString()}`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="files">
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="files" className="text-sm">
                <HardDrive className="w-4 h-4 mr-2" />
                Arquivos Recentes
              </TabsTrigger>
              <TabsTrigger value="apis" className="text-sm">
                <Send className="w-4 h-4 mr-2 text-sky-600" />
                Requisições Recentes
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="files" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Arquivos Recentes</h2>
                <Link to={createPageUrl("Upload")}>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Arquivo
                  </Button>
                </Link>
              </div>
              
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse mb-2" />
                          <div className="h-3 bg-gray-200 rounded w-1/4 animate-pulse" />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : files.length > 0 ? (
                <div className="space-y-4">
                  {files.map((file) => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Card className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-medium">{file.original_name}</h3>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>{formatFileSize(file.file_size)}</span>
                                <span>•</span>
                                <span>{formatDate(file.created_date)}</span>
                              </div>
                            </div>
                          </div>
                          <Link to={createPageUrl("UploadedFiles")}>
                            <Button variant="ghost" size="icon">
                              <ArrowUpRight className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium">Nenhum arquivo encontrado</h3>
                  <p className="text-gray-500 mb-4">
                    Comece fazendo upload de arquivos
                  </p>
                  <Link to={createPageUrl("Upload")}>
                    <Button>Fazer Upload</Button>
                  </Link>
                </Card>
              )}
              
              {files.length > 0 && (
                <div className="flex justify-center mt-4">
                  <Link to={createPageUrl("UploadedFiles")}>
                    <Button variant="outline">
                      Ver Todos os Arquivos
                      <ArrowUpRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="apis" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Requisições Recentes</h2>
                <Link to={createPageUrl("ApiTester")}>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Requisição
                  </Button>
                </Link>
              </div>
              
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse mb-2" />
                          <div className="h-3 bg-gray-200 rounded w-1/4 animate-pulse" />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : requests.length > 0 ? (
                <div className="space-y-4">
                  {requests.map((request) => (
                    <motion.div
                      key={request.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Card className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                              <Send className="w-5 h-5 text-sky-600" />
                            </div>
                            <div>
                              <h3 className="font-medium">{request.name}</h3>
                              <div className="flex items-center gap-2 text-sm">
                                <Badge className={getMethodColor(request.method)}>
                                  {request.method}
                                </Badge>
                                <span className="text-gray-500">{formatDate(request.created_date)}</span>
                              </div>
                            </div>
                          </div>
                          <Link to={createPageUrl("ApiTester")}>
                            <Button variant="ghost" size="icon">
                              <ArrowUpRight className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center">
                  <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium">Nenhuma requisição encontrada</h3>
                  <p className="text-gray-500 mb-4">
                    Comece criando requisições API
                  </p>
                  <Link to={createPageUrl("ApiTester")}>
                    <Button>Criar Requisição</Button>
                  </Link>
                </Card>
              )}
              
              {requests.length > 0 && (
                <div className="flex justify-center mt-4">
                  <Link to={createPageUrl("ApiTester")}>
                    <Button variant="outline">
                      Ver Todas as Requisições
                      <ArrowUpRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Activity Feed */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                <History className="w-5 h-5" />
                Atividades Recentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-gray-200 mt-2"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute left-1.5 top-0 bottom-0 w-0.5 bg-gray-100"></div>
                  <div className="space-y-6">
                    {stats.recentActivity.map((activity, index) => (
                      <div key={index} className="flex gap-3">
                        <div className={`w-3 h-3 rounded-full mt-1.5 ${
                          activity.type === 'file' ? 'bg-blue-400' : 'bg-indigo-400'
                        }`}></div>
                        <div>
                          <p className="text-sm">
                            {activity.type === 'file' 
                              ? `Arquivo "${activity.item.original_name}" foi ${formatFileSize(activity.item.file_size)}`
                              : `Requisição ${activity.item.method} para "${activity.item.name}"`
                            }
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(activity.item.created_date)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}